import {
  priceQuotationLine,
  quotationTotals,
  type ManualQuotationItem,
  type QuotationAmbiguousCandidate,
  type QuotationPreviewResult,
  type QuotationResource,
  type QuotationTotals,
} from '@neuroedge/shared';

/**
 * One editable quotation line as the editor holds it - numbers kept as the
 * typed strings so a half-typed value ("1.", "") doesn't jump around.
 * `productServiceId: ''` = price on request (no catalog product).
 */
export interface EditorRow {
  key: string;
  rawText: string;
  /** Free name for a price-on-request line; ignored for a product line. */
  name: string;
  productServiceId: string;
  productName: string;
  quantity: string;
  unit: string | null;
  unitPrice: string;
  discountPercent: string;
  /** AI's shortlist when it couldn't decide (shown first in the picker). */
  candidates: QuotationAmbiguousCandidate[];
  /** AI couldn't decide and nobody has picked yet. */
  needsPick: boolean;
}

let nextKey = 0;
const newKey = () => `row-${++nextKey}`;

const num = (s: string) => (s.trim() === '' ? NaN : Number(s));

export function blankRow(): EditorRow {
  return {
    key: newKey(),
    rawText: '',
    name: '',
    productServiceId: '',
    productName: '',
    quantity: '1',
    unit: null,
    unitPrice: '',
    discountPercent: '0',
    candidates: [],
    needsPick: false,
  };
}

export function rowsFromQuotation(q: QuotationResource): EditorRow[] {
  return [
    ...q.matchedItems.map((m) => ({
      ...blankRow(),
      rawText: m.rawText,
      name: m.productName,
      productServiceId: m.productServiceId,
      productName: m.productName,
      quantity: String(m.quantity),
      unit: m.unit,
      unitPrice: String(m.unitPrice),
      discountPercent: String(m.discountPercent ?? 0),
    })),
    ...q.unmatchedItems.map((u) => ({
      ...blankRow(),
      rawText: u.rawText,
      name: u.normalizedName,
      quantity: String(u.quantity),
      unit: u.unit,
    })),
  ];
}

export function rowsFromPreview(p: QuotationPreviewResult): EditorRow[] {
  return [
    ...p.matched.map((m) => ({
      ...blankRow(),
      rawText: m.rawText,
      name: m.productName,
      productServiceId: m.productServiceId,
      productName: m.productName,
      quantity: String(m.quantity),
      unit: m.unit,
      unitPrice: String(m.unitPrice),
      discountPercent: String(m.discountPercent ?? 0),
    })),
    ...p.ambiguous.map((a) => ({
      ...blankRow(),
      rawText: a.rawText,
      name: a.normalizedName,
      quantity: String(a.quantity),
      unit: a.unit,
      candidates: a.candidates,
      needsPick: true,
    })),
    ...p.unmatched.map((u) => ({
      ...blankRow(),
      rawText: u.rawText,
      name: u.normalizedName,
      quantity: String(u.quantity),
      unit: u.unit,
    })),
  ];
}

const isPriced = (r: EditorRow) => r.productServiceId !== '';

/** Live amounts for one row - null while it's price on request or a number is mid-typing. */
export function rowAmounts(r: EditorRow) {
  if (!isPriced(r)) return null;
  const unitPrice = num(r.unitPrice);
  const quantity = num(r.quantity);
  const discountPercent = num(r.discountPercent);
  if ([unitPrice, quantity, discountPercent].some((n) => Number.isNaN(n))) return null;
  return priceQuotationLine({ unitPrice, quantity, discountPercent });
}

export function editorTotals(rows: EditorRow[]): QuotationTotals {
  return quotationTotals(
    rows
      .filter((r) => rowAmounts(r) !== null)
      .map((r) => ({
        unitPrice: num(r.unitPrice),
        quantity: num(r.quantity),
        discountPercent: num(r.discountPercent),
      })),
  );
}

/** Sets the same discount on every priced line ("overall discount"). */
export function applyOverallDiscount(rows: EditorRow[], percent: string): EditorRow[] {
  return rows.map((r) => (isPriced(r) ? { ...r, discountPercent: percent } : r));
}

/** Per-field errors keyed `${index}.${field}`, mirroring the API's limits. */
export function validateRows(rows: EditorRow[]): Record<string, string> {
  const errors: Record<string, string> = {};
  if (rows.length === 0) errors.items = 'Add at least one item.';
  rows.forEach((r, i) => {
    const qty = num(r.quantity);
    if (Number.isNaN(qty) || qty <= 0) errors[`${i}.quantity`] = 'Enter a quantity above 0.';
    else if (qty > 1_000_000) errors[`${i}.quantity`] = 'Too large.';
    if (isPriced(r)) {
      const price = num(r.unitPrice);
      if (Number.isNaN(price) || price < 0)
        errors[`${i}.unitPrice`] = 'Enter a price of 0 or more.';
      const disc = num(r.discountPercent);
      if (Number.isNaN(disc) || disc < 0 || disc > 100) {
        errors[`${i}.discountPercent`] = 'Use 0 to 100.';
      }
    } else if (!r.name.trim()) {
      errors[`${i}.name`] = 'Pick a product or enter a name.';
    }
  });
  return errors;
}

/** The API payload (`POST /quotations/generate` / `PUT /quotations/:id/items`). */
export function toItems(rows: EditorRow[]): ManualQuotationItem[] {
  return rows.map((r) => {
    const label = isPriced(r) ? r.productName : r.name.trim();
    const base = {
      rawText: (r.rawText || label).slice(0, 500),
      normalizedName: label.slice(0, 500),
      quantity: num(r.quantity),
      unit: r.unit,
    };
    return isPriced(r)
      ? {
          ...base,
          productServiceId: r.productServiceId,
          unitPrice: num(r.unitPrice),
          discountPercent: num(r.discountPercent),
        }
      : { ...base, productServiceId: null };
  });
}
